import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  MessageSquare,
  Send,
} from 'lucide-react';

import { Markdown } from '@/components/common/Markdown';
import { HobbesAvatar } from '@/components/interview/HobbesAvatar';
import { ReviewView } from '@/components/interview/ReviewView';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuthApi';
import { ChatMessage, usePublicInterview } from '@/hooks/usePublicInterview';
import type { InterviewerDetail } from '@/lib/api/onboarding';
import { cn } from '@/lib/utils';

interface TrainingAgentDialogProps {
  interviewer: InterviewerDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrainingUpdated?: () => void;
  mode?: 'general' | 'open_questions';
}

const buildTrainingIntro = (
  interviewer: InterviewerDetail,
  mode: 'general' | 'open_questions'
) => {
  const openQuestions = interviewer.knowledge_profile?.open_questions?.length ?? 0;
  const latestSummary = interviewer.prompt_impact_summary?.summary;

  if (mode === 'open_questions' && openQuestions > 0) {
    return `Let’s close the remaining ${openQuestions} follow-up question${openQuestions === 1 ? '' : 's'} one by one. I’ll ask directly where the training still has gaps.`;
  }
  if (openQuestions > 0) {
    return `I reviewed the latest knowledge and still need help with ${openQuestions} follow-up question${openQuestions === 1 ? '' : 's'}.`;
  }
  if (latestSummary) {
    return latestSummary;
  }
  return 'Keep training the workspace like you would brief a strong sales rep. I’ll build on what we already know.';
};

const TrainingMessageBubble: React.FC<{
  message: ChatMessage;
  isLastMessage: boolean;
  logoUrl?: string | null;
  onSelectSuggestion: (answer: string) => void;
  onSubmitMultiSelect: (answers: string[]) => void;
}> = ({ message, isLastMessage, logoUrl, onSelectSuggestion, onSubmitMultiSelect }) => {
  const isInterviewer = message.speaker === 'interviewer';
  const isMulti = message.suggestionSelectionType === 'multi';
  const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set());
  const suggestionSignature = message.suggestedAnswers?.join('||') ?? '';

  useEffect(() => {
    setSelectedAnswers(new Set());
  }, [message.id, suggestionSignature, message.suggestionSelectionType]);

  const showSuggestions =
    isInterviewer &&
    isLastMessage &&
    (message.suggestedAnswers?.length ?? 0) > 0 &&
    !message.isStreaming;

  return (
    <div className={cn('flex gap-3', !isInterviewer && 'flex-row-reverse')}>
      {isInterviewer ? (
        <HobbesAvatar size="sm" />
      ) : (
        <HobbesAvatar size="sm" logoUrl={logoUrl} />
      )}

      <div className={cn('max-w-[85%] space-y-3', !isInterviewer && 'items-end')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3 text-sm shadow-sm',
            isInterviewer
              ? 'bg-muted text-foreground'
              : 'bg-primary text-primary-foreground'
          )}
        >
          {message.content ? (
            isInterviewer ? (
              <Markdown content={message.content} className="prose-sm" />
            ) : (
              <p className="whitespace-pre-wrap">{message.content}</p>
            )
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Thinking…</span>
            </div>
          )}
        </div>

        {showSuggestions && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {message.suggestedAnswers?.map((answer) => {
                const isSelected = selectedAnswers.has(answer);
                return (
                  <Button
                    key={answer}
                    type="button"
                    variant={isMulti && isSelected ? 'default' : 'outline'}
                    size="sm"
                    className="h-auto whitespace-normal text-left"
                    onClick={() => {
                      if (!isMulti) {
                        onSelectSuggestion(answer);
                        return;
                      }
                      setSelectedAnswers((current) => {
                        const next = new Set(current);
                        if (next.has(answer)) {
                          next.delete(answer);
                        } else {
                          next.add(answer);
                        }
                        return next;
                      });
                    }}
                  >
                    {answer}
                  </Button>
                );
              })}
            </div>

            {isMulti && (
              <Button
                type="button"
                size="sm"
                disabled={selectedAnswers.size === 0}
                onClick={() => {
                  onSubmitMultiSelect(Array.from(selectedAnswers));
                  setSelectedAnswers(new Set());
                }}
              >
                Use Selected Answers
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const TrainingAgentDialog: React.FC<TrainingAgentDialogProps> = ({
  interviewer,
  open,
  onOpenChange,
  onTrainingUpdated,
  mode = 'general',
}) => {
  const { user, userData } = useAuth();
  const email = userData?.email || user?.email || '';
  const name = userData?.displayName || user?.displayName || undefined;
  const intro = useMemo(() => buildTrainingIntro(interviewer, mode), [interviewer, mode]);
  const [input, setInput] = useState('');
  const [didNotifyUpdate, setDidNotifyUpdate] = useState(false);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);

  const {
    phase,
    interviewerInfo,
    messages,
    summary,
    error,
    currentTopicIndex,
    totalTopics,
    topics,
    startOrResume,
    sendMessage,
    selectSuggestedAnswer,
    submitMultiSelectAnswers,
    skipTopic,
    enterReview,
    reviewSummaries,
    topicConfidence,
    updateReviewTopic,
    confirmReview,
    backToChat,
    isStreaming,
    isNavigating,
    isGeneratingReview,
    isUpdatingReview,
    isConfirming,
  } = usePublicInterview(open ? interviewer.token : '');

  useEffect(() => {
    if (!open || !email) return;
    if (phase !== 'email') return;
    void startOrResume(email, name, { forceNew: mode === 'open_questions' });
  }, [open, email, name, mode, phase, startOrResume]);

  useEffect(() => {
    if (!open) {
      setInput('');
      setDidNotifyUpdate(false);
    }
  }, [open]);

  useEffect(() => {
    if (phase === 'complete' && !didNotifyUpdate) {
      setDidNotifyUpdate(true);
      onTrainingUpdated?.();
    }
  }, [didNotifyUpdate, onTrainingUpdated, phase]);

  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, [messages, phase]);

  const handleSubmit = async () => {
    const next = input.trim();
    if (!next || isStreaming) return;
    setInput('');
    await sendMessage(next);
  };

  const openQuestionsCount = interviewer.knowledge_profile?.open_questions?.length ?? 0;
  const displayOpenQuestionsCount =
    mode === 'open_questions' && phase === 'complete' ? 0 : openQuestionsCount;
  const currentTopicName = topics[currentTopicIndex]?.name;
  const lastMessageId = messages[messages.length - 1]?.id;
  const displayName = interviewerInfo?.name || interviewer.name || 'Hobbes Training';
  const headerTitle = mode === 'open_questions' ? 'Answer Open Questions' : displayName;
  const displayIntro =
    phase === 'complete'
      ? 'The latest follow-up has been merged back into the training workspace.'
      : intro;
  const displayDescription =
    interviewerInfo?.description
    || interviewer.description
    || 'We’ll build on what the workspace already knows and only follow up where context is missing.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[92vh] max-w-6xl gap-0 overflow-hidden p-0">
        <div className="grid h-full min-h-0 lg:grid-cols-[0.78fr_0.22fr]">
          <div className="flex min-h-0 flex-col border-r">
            <DialogHeader className="border-b bg-background px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <HobbesAvatar size="md" />
                    <div className="space-y-1">
                      <DialogTitle className="text-xl">{headerTitle}</DialogTitle>
                      <DialogDescription className="max-w-3xl">
                        {displayDescription}
                      </DialogDescription>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="default">
                      {mode === 'open_questions' ? 'Open Question Follow-up' : 'Continue Training'}
                    </Badge>
                    {displayOpenQuestionsCount > 0 && (
                      <Badge variant="secondary">
                        {displayOpenQuestionsCount} follow-up question{displayOpenQuestionsCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {totalTopics > 0 && phase !== 'reviewing' && phase !== 'complete' && (
                      <Badge variant="outline">
                        Topic {Math.min(currentTopicIndex + 1, totalTopics)} of {totalTopics}
                      </Badge>
                    )}
                  </div>
                  <p className="max-w-3xl text-sm text-muted-foreground">
                    {displayIntro}
                  </p>
                </div>

                {phase === 'chatting' && (
                  <div className="flex items-center gap-2">
                    {totalTopics > 0 && currentTopicIndex < totalTopics - 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={skipTopic}
                        disabled={isStreaming || isNavigating}
                      >
                        Next Topic
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={enterReview}
                      disabled={isStreaming || isNavigating}
                    >
                      Review and Finish
                    </Button>
                  </div>
                )}
              </div>
            </DialogHeader>

            {error && (
              <div className="border-b px-6 py-3">
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </div>
            )}

            {!email && (
              <div className="border-b px-6 py-3">
                <Alert variant="destructive">
                  <AlertDescription>
                    You need an account email to continue training from the platform.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <div className="min-h-0 flex-1">
              {(phase === 'loading' || phase === 'starting' || phase === 'generating_review' || phase === 'confirming') && (
                <div className="flex h-full items-center justify-center">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {phase === 'confirming'
                      ? 'Finalizing the training update…'
                      : phase === 'generating_review'
                      ? 'Reviewing what we learned…'
                      : 'Opening your training conversation…'}
                  </div>
                </div>
              )}

              {phase === 'reviewing' && (
                <ReviewView
                  summaries={reviewSummaries}
                  topicConfidence={topicConfidence}
                  onUpdateTopic={updateReviewTopic}
                  onConfirm={confirmReview}
                  onBackToChat={backToChat}
                  isGenerating={isGeneratingReview}
                  isUpdating={isUpdatingReview}
                  isConfirming={isConfirming}
                  interviewerName={interviewerInfo?.name}
                  logoUrl={interviewerInfo?.logoUrl}
                />
              )}

              {phase === 'complete' && (
                <div className="h-full overflow-y-auto px-6 py-6">
                  <div className="mx-auto w-full max-w-3xl rounded-2xl border bg-muted/20 p-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      <div>
                        <h3 className="text-lg font-semibold">Training updated</h3>
                        <p className="text-sm text-muted-foreground">
                          I merged the latest conversation into canonical knowledge.
                        </p>
                      </div>
                    </div>
                    {summary && (
                      <div className="mt-5 rounded-xl bg-background p-4">
                        <Markdown content={summary} className="prose-sm" />
                      </div>
                    )}
                    <div className="mt-5 flex justify-end">
                      <Button onClick={() => onOpenChange(false)}>Close</Button>
                    </div>
                  </div>
                </div>
              )}

              {phase === 'chatting' && (
                <div ref={scrollViewportRef} className="h-full overflow-y-auto">
                  <div className="mx-auto flex max-w-[720px] flex-col gap-4 px-4 py-6">
                    {messages.length === 0 && (
                      <div className="rounded-2xl border border-dashed p-6 text-sm text-muted-foreground">
                        I’m getting the training conversation ready.
                      </div>
                    )}

                    {messages.map((message) => (
                      <TrainingMessageBubble
                        key={message.id}
                        message={message}
                        isLastMessage={message.id === lastMessageId}
                        logoUrl={interviewerInfo?.logoUrl}
                        onSelectSuggestion={selectSuggestedAnswer}
                        onSubmitMultiSelect={submitMultiSelectAnswers}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {phase === 'chatting' && (
              <div className="border-t bg-background px-6 py-4">
                <div className="mx-auto flex max-w-[720px] items-end gap-3">
                  <Textarea
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void handleSubmit();
                      }
                    }}
                    placeholder="Answer in your own words. I’ll update the training from here."
                    rows={3}
                    disabled={isStreaming}
                  />
                  <Button
                    className="shrink-0"
                    onClick={() => void handleSubmit()}
                    disabled={!input.trim() || isStreaming}
                  >
                    {isStreaming ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <aside className="hidden min-h-0 flex-col bg-muted/10 lg:flex">
            <div className="border-b px-5 py-4">
              <p className="text-sm font-medium">Training context</p>
              <p className="mt-1 text-sm text-muted-foreground">
                I’ll build on the workspace’s existing knowledge instead of starting from scratch.
              </p>
            </div>

            <div className="space-y-4 overflow-y-auto px-5 py-5 text-sm">
              <div className="rounded-xl border bg-background p-4">
                <p className="font-medium">Current focus</p>
                <p className="mt-1 text-muted-foreground">
                  {phase === 'reviewing'
                    ? 'Review the generated summaries and make any corrections before merging them.'
                    : currentTopicName || 'Opening the next follow-up topic.'}
                </p>
              </div>

              <div className="rounded-xl border bg-background p-4">
                <p className="font-medium">Knowledge status</p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Canonical version</span>
                    <span className="font-medium">v{interviewer.knowledge_profile?.version ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Confirmed topics</span>
                    <span className="font-medium">
                      {Object.keys(interviewer.knowledge_profile?.confirmed_topic_summaries ?? {}).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Open questions</span>
                    <span className="font-medium">{displayOpenQuestionsCount}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border bg-background p-4">
                <p className="font-medium">How this works</p>
                <div className="mt-3 space-y-3 text-muted-foreground">
                  <div className="flex gap-2">
                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>I ask only about gaps or new source material.</p>
                  </div>
                  <div className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>Confirmed answers merge back into the canonical training profile.</p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};
